import React from 'react';
import styled from 'styled-components';
import { CardRow } from '../../../../../generic/ui/CardRow';
import { Mono } from '../../../../../generic/ui/Mono';
import { Tag } from '../../../../../generic/ui/Tag';
import { StatusBadge } from '../../../../../generic/ui/StatusBadge';
import InvoiceIcon from '../../../../../generic/icons/IconLib/InvoiceIcon';
import CalendarIcon from '../../../../../generic/icons/IconLib/CalendarIcon';
import UserIcon from '../../../../../generic/icons/IconLib/UserIcon';
import SproutIcon from '../../../../../generic/icons/IconLib/SproutIcon';
import PalletIcon from '../../../../../generic/icons/IconLib/PalletIcon';
import MapPinIcon from '../../../../../generic/icons/IconLib/MapPinIcon';
import ScaleIcon from '../../../../../generic/icons/IconLib/ScaleIcon';
import PackageBoxIcon from '../../../../../generic/icons/IconLib/PackageBoxIcon';
import BoxesIcon from '../../../../../generic/icons/IconLib/BoxesIcon';

const CUSTOMER_ID_COLOR = '#8CC53D';

export interface LikutOrderRowStatus {
    label: string;
    bg: string;
}

export interface LikutOrderRowProps {
    orderId: number;
    dateLabel: string;
    clientName: string;
    customerId?: number;
    groupDesc?: string;
    groupColor?: string;
    rampId?: number | string;
    products: number;
    pallets: number;
    weightLabel: string;
    packagesLabel: string;
    piecesLabel: string;
    status?: LikutOrderRowStatus;
    isLast?: boolean;
    onClick?: () => void;
    className?: string;
}

const LikutOrderRow: React.FC<LikutOrderRowProps> = ({
    orderId,
    dateLabel,
    clientName,
    customerId,
    groupDesc,
    groupColor,
    rampId,
    products,
    pallets,
    weightLabel,
    packagesLabel,
    piecesLabel,
    status,
    isLast = false,
    onClick,
    className,
}) => {
    const interactive = typeof onClick === 'function';
    const handleKeyDown = interactive
        ? (e: React.KeyboardEvent<HTMLDivElement>) => {
              if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  onClick?.();
              }
          }
        : undefined;

    return (
        <CardRow
            className={className}
            $columns='7rem minmax(0, 1fr) auto'
            $mobileColumns='auto minmax(0, 1fr) auto'
            $isLast={isLast}
            $interactive={interactive}
            onClick={onClick}
            onKeyDown={handleKeyDown}
            role={interactive ? 'button' : undefined}
            tabIndex={interactive ? 0 : undefined}
        >
            <CardRow.Cell>
                <CardRow.Stack $gap='0.125rem'>
                    <CardRow.Line $gap='0.375rem'>
                        <IdIcon>
                            <InvoiceIcon color='currentColor' size={16} />
                        </IdIcon>
                        <Id>{orderId}</Id>
                    </CardRow.Line>
                    <CardRow.Line $gap='0.375rem'>
                        <DateIcon>
                            <CalendarIcon color='currentColor' size={14} />
                        </DateIcon>
                        <When>{dateLabel}</When>
                    </CardRow.Line>
                    {status && (
                        <StatusBadge label={status.label} color={status.bg} multiline />
                    )}
                </CardRow.Stack>
            </CardRow.Cell>

            <CardRow.Cell>
                <CardRow.Stack $gap='0.25rem'>
                    <CardRow.Line $gap='0.5rem'>
                        {/* <NameIcon>
                            <UserIcon color='currentColor' size={16} />
                        </NameIcon> */}
                        
                        <PrimaryName>{clientName}</PrimaryName>
                       
                    </CardRow.Line>
                    <CardRow.Line $gap='0.5rem' $wrap>
                    {customerId != null && (
                            <Tag variant='tinted' color={CUSTOMER_ID_COLOR}>
                                {customerId}
                            </Tag>
                        )}
                        {rampId != null && rampId !== '' && (
                            <MetaItem>
                                <MetaIcon>
                                    <MapPinIcon color='currentColor' size={14} />
                                </MetaIcon>
                                <MetaText>{rampId}</MetaText>
                            </MetaItem>
                        )}
                        <MetaItem>
                            <MetaIcon>
                                <PalletIcon color='currentColor' size={14} />
                            </MetaIcon>
                            <MetaValue>{pallets}</MetaValue>
                        </MetaItem>
                    </CardRow.Line>
                    <CardRow.Line $gap='0.5rem' $wrap>
                        <MetaItem>
                            <MetaIcon>
                                <SproutIcon color='currentColor' size={14} />
                            </MetaIcon>
                            <MetaText>פריטים</MetaText>
                            <MetaValue>{products}</MetaValue>
                        </MetaItem>
                        
                        {groupDesc && (
                            <Tag variant='tinted' color={groupColor}>
                                {groupDesc}
                            </Tag>
                        )}
                    </CardRow.Line>
                </CardRow.Stack>
            </CardRow.Cell>

            <CardRow.Cell $align='end'>
                <MetricsGrid>
                    <MetaIcon>
                        <ScaleIcon color='currentColor' size={14} />
                    </MetaIcon>
                    <MetricValue>{weightLabel}</MetricValue>
                    <MetaIcon>
                        <PackageBoxIcon color='currentColor' size={14} />
                    </MetaIcon>
                    <MetricValue>{packagesLabel}</MetricValue>
                    <MetaIcon>
                        <BoxesIcon color='currentColor' size={14} />
                    </MetaIcon>
                    <MetricValue>{piecesLabel}</MetricValue>
                </MetricsGrid>
            </CardRow.Cell>
        </CardRow>
    );
};

export default LikutOrderRow;

const IdIcon = styled.span`
    display: inline-flex;
    align-items: center;
    color: ${({ theme }) => theme.colors.ink2};
    flex-shrink: 0;
`;

const NameIcon = styled.span`
    display: inline-flex;
    align-items: center;
    color: ${({ theme }) => theme.colors.ink3};
    flex-shrink: 0;
`;

const Id = styled(Mono)`
    font-size: ${({ theme }) => theme.text.lg};
    font-weight: 600;
    color: ${({ theme }) => theme.colors.ink};
    @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
        font-size: ${({ theme }) => theme.textMobile.lg};
    }
`;

const When = styled(Mono)`
    font-size: ${({ theme }) => theme.text.sm};
    font-weight: 400;
    color: ${({ theme }) => theme.colors.ink4};
    @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
        font-size: ${({ theme }) => theme.textMobile.sm};
    }
`;

const DateIcon = styled.span`
    display: inline-flex;
    align-items: center;
    color: ${({ theme }) => theme.colors.ink4};
    flex-shrink: 0;
`;

const PrimaryName = styled.span`
    font-size: ${({ theme }) => theme.text.lg};
    color: ${({ theme }) => theme.colors.ink};
    font-weight: 500;
    white-space: nowrap;
    font-family: ${({ theme }) => theme.fonts.sans};
    overflow: hidden;
    text-overflow: ellipsis;
    max-width: 100%;
    @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
        font-size: ${({ theme }) => theme.textMobile.lg};
    }
`;

const MetaItem = styled.span`
    display: inline-flex;
    align-items: center;
    gap: 0.25rem;
`;

const MetaIcon = styled.span`
    display: inline-flex;
    align-items: center;
    color: ${({ theme }) => theme.colors.ink3};
    flex-shrink: 0;
`;

const MetaText = styled.span`
    font-size: ${({ theme }) => theme.text.sm};
    color: ${({ theme }) => theme.colors.ink3};
    font-family: ${({ theme }) => theme.fonts.sans};
    white-space: nowrap;
    @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
        font-size: ${({ theme }) => theme.textMobile.sm};
    }
`;

const MetaValue = styled(Mono)`
    font-size: ${({ theme }) => theme.text.sm};
    color: ${({ theme }) => theme.colors.ink2};
    white-space: nowrap;
    @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
        font-size: ${({ theme }) => theme.textMobile.sm};
    }
`;

const MetricsGrid = styled.div`
    display: grid;
    grid-template-columns: auto auto;
    align-items: center;
    column-gap: 0.375rem;
    row-gap: 0.125rem;
    padding-inline: 0.25rem 0.5rem;
`;

const MetricValue = styled(Mono)`
    justify-self: end;
    text-align: end;
    font-variant-numeric: tabular-nums;
    font-size: ${({ theme }) => theme.text.md};
    color: ${({ theme }) => theme.colors.ink2};
    white-space: nowrap;
    @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
        font-size: ${({ theme }) => theme.textMobile.md};
    }
`;
